import { Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';
import { useLacKnowledgeBaseDocumentAnalyticsQuery } from '../../features/lac/queries/useLacKnowledgeBaseDocumentAnalyticsQuery';
import { useI18n } from '../../i18n/useI18n';
import { resolveLacTenantPath } from '../../routes/lacPaths';
import type { LacTenantOverviewDateRange } from '../../types/lac';
import { formatBytes, formatNumber } from '../../utils/formatUtils';
import './LacTenantStatsDocumentsTeaser.css';

type LacTenantStatsDocumentsTeaserProps = {
  tenantName: string;
  dateRange?: LacTenantOverviewDateRange;
};

export function LacTenantStatsDocumentsTeaser({
  tenantName,
  dateRange,
}: LacTenantStatsDocumentsTeaserProps) {
  const { locale, translate } = useI18n();
  const analyticsQuery = useLacKnowledgeBaseDocumentAnalyticsQuery(
    tenantName,
    dateRange,
  );
  const row = analyticsQuery.data?.[0];
  const documentCount = row?.documentCount ?? 0;
  const totalSize = row?.totalFileSizeBytes ?? 0;
  const documentsPath = resolveLacTenantPath(tenantName, 'documents');

  return (
    <section
      className="lac-tenant-stats-documents-teaser"
      aria-label={translate('lac.organisations.stats.documents.legend')}
    >
      <div className="lac-tenant-stats-documents-teaser__header">
        <div className="lac-tenant-stats-section__title-row">
          <h2 className="lac-tenant-overview__title">
            {translate('lac.organisations.stats.documents.title')}
          </h2>
          <LiksTooltip
            className="lac-tenant-stats-section__info"
            content={translate('lac.organisations.stats.documents.info')}
            placement="top-start"
            describeChildren
          >
            <Info
              className="lac-tenant-stats-section__info-icon"
              strokeWidth={2}
              aria-hidden
            />
          </LiksTooltip>
        </div>
        <Link
          className="lac-tenant-stats-documents-teaser__link"
          to={documentsPath}
        >
          {translate('lac.organisations.stats.documents.viewAll')}
        </Link>
      </div>

      {analyticsQuery.isLoading ? (
        <p className="lac-tenant-stats-documents-teaser__status">
          {translate('lac.organisations.stats.documents.loading')}
        </p>
      ) : null}

      {analyticsQuery.isError ? (
        <p className="lac-tenant-stats-documents-teaser__status" role="alert">
          {translate('lac.organisations.stats.documents.loadError')}
        </p>
      ) : null}

      {!analyticsQuery.isLoading && !analyticsQuery.isError ? (
        <div className="lac-tenant-stats-documents-teaser__metrics">
          <div className="lac-tenant-stats-documents-teaser__metric">
            <span className="lac-tenant-stats-documents-teaser__metric-label">
              {translate('lac.organisations.stats.documents.documentCount')}
            </span>
            <span className="lac-tenant-stats-documents-teaser__metric-value">
              {formatNumber(documentCount, locale)}
            </span>
          </div>
          <div className="lac-tenant-stats-documents-teaser__metric">
            <span className="lac-tenant-stats-documents-teaser__metric-label">
              {translate('lac.organisations.stats.documents.totalSize')}
            </span>
            <span className="lac-tenant-stats-documents-teaser__metric-value">
              {formatBytes(totalSize, locale)}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
